/**
 * Skills API — transport, strict normalizer, and adapter for the
 * B-2.C skills / preferences endpoints.
 *
 * Contract authority (FROZEN; consumed as git object, NOT reconstructed):
 *   - origin/act/b2-c-skills-preferences-backend
 *     @ 7aa71c7109ba694396c90b876934949c63ee03eb
 *   - docs/epics/profile-questionnaire-detail-slice-b-2/
 *       b2-c-skills-contract01.md  (backend contract)
 *   - docs/epics/profile-questionnaire-detail-slice-b-2/
 *       b2-c-frontend-contract01.md  (FE consumption contract)
 *
 * Per ACT-INDEEP-B2-C-SKILLS-PREFERENCES-FRONTEND01 §0.6 and §0.7:
 *
 *   SKILL_IDENTITY             = server-owned UUID
 *   LABELS                     = server supplied (NEVER hardcoded)
 *   TAXONOMY_RUNTIME_MUTABILITY = NONE in B-2.C
 *   MAX_SELECTED                = 10
 *   SELECTION_SEMANTICS         = REPLACE ENTIRE selection
 *
 *   GET  /api/v1/skills
 *   GET  /api/v1/{friend|sponsor}/profile/skills
 *   POST /api/v1/{friend|sponsor}/profile/skills   // { skill_ids: string[] }
 *
 * The FE never sends profile_id or account_id on the POST envelope
 * (BOLA invariant; mirrors backend §9).
 */

import { getCSRFToken, CSRF_HEADER_NAME } from './auth';

// =============================================================================
// Frozen wire constants
// =============================================================================

const API_BASE = '/api/v1' as const;

/** Maximum selected skills (server-enforced). Mirrors backend 10. */
export const MAX_SELECTED_SKILLS = 10 as const;

/** Default taxonomy page size. Mirrors backend default 50. */
export const SKILLS_DEFAULT_LIMIT = 50 as const;

/** Hard ceiling. Mirrors backend max 100. */
export const SKILLS_MAX_LIMIT = 100 as const;

// =============================================================================
// Transport DTOs
// =============================================================================

export interface Skill {
  readonly id: string;
  readonly code: string;
  readonly label_ru: string;
  readonly category: string;
  readonly popularity: number;
  readonly is_active: boolean;
}

export interface SkillsResponse {
  readonly skills: readonly Skill[];
  readonly limit: number;
  readonly offset: number;
  readonly has_more: boolean;
}

export interface ProfileSkillsResponse {
  readonly skills: readonly Skill[];
}

export interface SetProfileSkillsRequest {
  readonly skill_ids: readonly string[];
}

// =============================================================================
// Error mapping
// =============================================================================

export class SkillsError extends Error {
  public readonly status: number;
  public readonly safe: boolean;

  constructor(message: string, status: number, safe: boolean) {
    super(message);
    this.name = 'SkillsError';
    this.status = status;
    this.safe = safe;
  }
}
// =============================================================================
// Strict normalizer (single source of truth)
// =============================================================================

/**
 * RFC 4122 canonical UUID textual form.
 *
 * Format: 8-4-4-4-12 lowercase / uppercase hex with dashes.
 *
 * Per RFC 4122 §3 + §4.1.1 (version field) + §4.1.3 (variant field):
 *   - The 13th hex digit (the first nibble of the third group) MUST
 *     encode the version: 1, 3, 4, 5, or (per RFC 9562 §5.1) 7.
 *   - The 17th hex digit (the first nibble of the fourth group) MUST
 *     encode the variant: 8, 9, a, or b (the RFC 4122 "10xx" pattern).
 *
 * The OpenAPI registered `format: uuid` is defined to mean "a UUID
 * according to RFC 4122". Therefore, accepting strings like
 * `b2c00000-0000-0000-0000-0000000000XX` (version nibble `0`, variant
 * nibble `0`) is incompatible with the declared OpenAPI contract even
 * though PostgreSQL's `uuid` column type would happily store them.
 *
 * CORRECTION02 R6 (full): the previous regex only enforced shape
 * (8-4-4-4-12 hex). It was too permissive. The FE's helper must
 * reject strings that look UUID-shaped but violate the RFC version /
 * variant bit pattern, otherwise the FE accepts IDs that the
 * declared OpenAPI schema forbids.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[13457][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuidLike(value: string): boolean {
  return UUID_RE.test(value);
}

export interface NormalizeSkillsResult {
  readonly skills: readonly Skill[];
  readonly violations: readonly string[];
}

export function normalizeSkill(
  input: unknown,
  index: number
): { readonly skill: Skill | null; readonly violation: string | null } {
  const v = (reason: string): string => `skills[${index}]: ${reason}`;
  if (input === null || typeof input !== 'object') {
    return { skill: null, violation: v('not an object') };
  }
  const obj = input as Record<string, unknown>;
  if (typeof obj.id !== 'string' || obj.id.length === 0) {
    return { skill: null, violation: v('id missing or not string') };
  }
  if (!isUuidLike(obj.id)) {
    return { skill: null, violation: v(`id is not UUID-shaped: ${obj.id}`) };
  }
  if (typeof obj.code !== 'string' || obj.code.length === 0) {
    return { skill: null, violation: v('code missing or not string') };
  }
  if (typeof obj.label_ru !== 'string' || obj.label_ru.length === 0) {
    return { skill: null, violation: v('label_ru missing or not string') };
  }
  if (typeof obj.category !== 'string' || obj.category.length === 0) {
    return { skill: null, violation: v('category missing or not string') };
  }
  if (typeof obj.popularity !== 'number' || !Number.isInteger(obj.popularity)) {
    return { skill: null, violation: v('popularity is not an integer') };
  }
  if (typeof obj.is_active !== 'boolean') {
    return { skill: null, violation: v('is_active is not a boolean') };
  }
  return {
    skill: {
      id: obj.id,
      code: obj.code,
      label_ru: obj.label_ru,
      category: obj.category,
      popularity: obj.popularity,
      is_active: obj.is_active,
    },
    violation: null,
  };
}

export function normalizeSkills(input: unknown): NormalizeSkillsResult {
  if (!Array.isArray(input)) {
    return { skills: [], violations: ['skills: not an array'] };
  }
  const skills: Skill[] = [];
  const violations: string[] = [];
  const seenIds = new Set<string>();
  for (let i = 0; i < input.length; i += 1) {
    const { skill, violation } = normalizeSkill(input[i], i);
    if (skill !== null) {
      if (seenIds.has(skill.id)) {
        violations.push(`skills[${i}]: duplicate id ${skill.id}`);
        continue;
      }
      seenIds.add(skill.id);
      skills.push(skill);
    } else if (violation !== null) {
      violations.push(violation);
    }
  }
  return { skills, violations };
}

/**
 * Strictly normalize a list of selected skill IDs (the FE-only mirror
 * of the server's POST envelope validation).
 *
 * Each ID MUST match the RFC 4122 canonical UUID textual form:
 * 8-4-4-4-12 hex with a version nibble in {1, 3, 4, 5, 7} and a
 * variant nibble in {8, 9, a, b}. This matches the OpenAPI
 * registered `format: uuid` semantic; it is NOT a permissive
 * "8-4-4-4-12 hex" check (that would let through identifiers that
 * look UUID-shaped but carry wrong version/variant bits and
 * therefore violate the declared OpenAPI contract).
 */
export function normalizeSelectedSkillIds(
  input: unknown
): { readonly ids: readonly string[]; readonly violations: readonly string[] } {
  if (!Array.isArray(input)) {
    return { ids: [], violations: ['skill_ids: not an array'] };
  }
  const ids: string[] = [];
  const violations: string[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < input.length; i += 1) {
    const v = input[i];
    if (typeof v !== 'string' || v.length === 0) {
      violations.push(`skill_ids[${i}]: not a non-empty string`);
      continue;
    }
    if (!isUuidLike(v)) {
      violations.push(`skill_ids[${i}]: not UUID-shaped: ${v}`);
      continue;
    }
    if (seen.has(v)) {
      violations.push(`skill_ids[${i}]: duplicate id ${v}`);
      continue;
    }
    seen.add(v);
    ids.push(v);
  }
  if (ids.length > MAX_SELECTED_SKILLS) {
    violations.push(
      `skill_ids: too many selected (${ids.length} > ${MAX_SELECTED_SKILLS})`
    );
  }
  return { ids, violations };
}

/**
 * Strict envelope normalizer for GET /api/v1/skills.
 *
 * Per backend contract §2.2, the response shape is:
 *
 *   { skills: Skill[]; limit: number; offset: number; has_more: boolean }
 *
 * All four fields are REQUIRED (no defaults; missing/wrong-type
 * produces a protocol violation, NOT a fabricated fallback).
 *
 * Range constraints mirror backend validation:
 *   - limit  : integer in 1..SKILLS_MAX_LIMIT (100)
 *   - offset : integer >= 0
 *   - has_more: boolean
 *
 * This addresses the CORRECTION01 review (R2): previous behavior
 * silently coerced missing `has_more` to `false` and never validated
 * `limit` / `offset`, which would have allowed the FE to consume
 * partially-shaped server responses without surfacing contract drift.
 */
export interface NormalizeSkillsResponseResult {
  readonly skills: readonly Skill[];
  readonly limit: number;
  readonly offset: number;
  readonly hasMore: boolean;
  readonly violations: readonly string[];
}

export function normalizeSkillsResponse(
  input: unknown
): NormalizeSkillsResponseResult {
  const violations: string[] = [];
  if (input === null || typeof input !== 'object') {
    violations.push('response: not an object');
    return { skills: [], limit: 0, offset: 0, hasMore: false, violations };
  }
  const obj = input as Record<string, unknown>;

  // skills (REQUIRED; array; per-element normalization handled below)
  if (!('skills' in obj)) {
    violations.push('skills: missing');
  }
  const skillsField = obj.skills;
  if (!Array.isArray(skillsField)) {
    violations.push('skills: not an array');
  }
  const { skills, violations: skillViolations } = normalizeSkills(
    Array.isArray(skillsField) ? skillsField : []
  );
  for (const v of skillViolations) violations.push(v);

  // limit (REQUIRED, integer in 1..SKILLS_MAX_LIMIT)
  let limit = 0;
  if (!('limit' in obj)) {
    violations.push('limit: missing');
  } else if (typeof obj.limit !== 'number' || !Number.isInteger(obj.limit)) {
    violations.push(`limit: not an integer (${typeof obj.limit})`);
  } else if (obj.limit < 1 || obj.limit > SKILLS_MAX_LIMIT) {
    violations.push(
      `limit: out of range (${obj.limit} not in 1..${SKILLS_MAX_LIMIT})`
    );
  } else {
    limit = obj.limit;
  }

  // offset (REQUIRED, integer >= 0)
  let offset = 0;
  if (!('offset' in obj)) {
    violations.push('offset: missing');
  } else if (typeof obj.offset !== 'number' || !Number.isInteger(obj.offset)) {
    violations.push(`offset: not an integer (${typeof obj.offset})`);
  } else if (obj.offset < 0) {
    violations.push(`offset: negative (${obj.offset})`);
  } else {
    offset = obj.offset;
  }

  // has_more (REQUIRED, boolean)
  let hasMore = false;
  if (!('has_more' in obj)) {
    violations.push('has_more: missing');
  } else if (typeof obj.has_more !== 'boolean') {
    violations.push(`has_more: not a boolean (${typeof obj.has_more})`);
  } else {
    hasMore = obj.has_more;
  }

  return { skills, limit, offset, hasMore, violations };
}

/**
 * Strict envelope normalizer for GET /api/v1/{friend|sponsor}/profile/skills
 * and POST /api/v1/{friend|sponsor}/profile/skills.
 *
 * Per backend contract §2.3, the response shape is:
 *
 *   { skills: Skill[] }
 *
 * The `skills` field is REQUIRED.
 */
export function normalizeProfileSkillsResponse(
  input: unknown
): { readonly skills: readonly Skill[]; readonly violations: readonly string[] } {
  if (input === null || typeof input !== 'object') {
    return { skills: [], violations: ['response: not an object'] };
  }
  const obj = input as Record<string, unknown>;
  if (!('skills' in obj)) {
    return { skills: [], violations: ['skills: missing'] };
  }
  if (!Array.isArray(obj.skills)) {
    return { skills: [], violations: ['skills: not an array'] };
  }
  return normalizeSkills(obj.skills);
}

// =============================================================================
// Transport helpers
// =============================================================================

export function csrfHeaders(): Readonly<Record<string, string>> {
  // ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01 (CORRECTION01):
  // skills.ts follows the synchronous cookie-only contract: read
  // the csrf_token cookie via `getCSRFToken()` and attach it as a
  // header if present. The defensive `GET /api/auth/csrf` bootstrap
  // is scoped to feed-posts in this ACT.
  //
  // Known follow-up (P2-01 cross-adapter backlog item):
  //   The backend's CSRFStore is single-use. If the cookie value has
  //   been consumed by a prior mutation, this will surface as
  //   `csrf token invalid`. Until skills migrates to
  //   `await ensureCSRFToken()` + per-mutation fresh-token fetch, it
  //   shares the same production failure mode that this ACT repaired
  //   for feed-posts.
  const token = getCSRFToken();
  return token === undefined
    ? { 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: token };
}

export function asJson(body: unknown): Response {
  return new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });
}

export function buildUrl(
  path: string,
  query?: Readonly<Record<string, string>>
): string {
  if (query === undefined) return `${API_BASE}${path}`;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v.length > 0) params.set(k, v);
  }
  const qs = params.toString();
  return qs.length === 0 ? `${API_BASE}${path}` : `${API_BASE}${path}?${qs}`;
}

/**
 * Wrap a fetch invocation into the canonical SkillsError envelope.
 * Never throws raw fetch errors; collapses them to safe=false.
 */
export async function skillsFetch(
  input: string,
  init: RequestInit,
  fallbackMessage: string
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(input, init);
  } catch {
    throw new SkillsError(fallbackMessage, 0, false);
  }
  if (response.ok) return response;
  const safe = response.status >= 400 && response.status < 500;
  let serverMessage = fallbackMessage;
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string' && body.message.length > 0) {
      serverMessage = body.message;
    }
  } catch {
    /* body not JSON; keep fallback */
  }
  throw new SkillsError(serverMessage, response.status, safe);
}
// =============================================================================
// Adapters (raw body; effect runners pipe through normalizeSkills)
// =============================================================================

export interface ListSkillsQuery {
  readonly category?: string;
  readonly limit?: number;
  readonly offset?: number;
}

export async function listSkillsRaw(
  query: ListSkillsQuery = {}
): Promise<unknown> {
  const params: Record<string, string> = {};
  if (query.category !== undefined && query.category.length > 0) {
    params.category = query.category;
  }
  if (typeof query.limit === 'number') {
    if (query.limit <= 0 || query.limit > SKILLS_MAX_LIMIT) {
      throw new SkillsError(
        `limit out of range (1..${SKILLS_MAX_LIMIT})`,
        400,
        true
      );
    }
    params.limit = String(query.limit);
  }
  if (typeof query.offset === 'number') {
    if (query.offset < 0) {
      throw new SkillsError('offset must be >= 0', 400, true);
    }
    params.offset = String(query.offset);
  }
  const response = await skillsFetch(
    buildUrl('/skills', params),
    { method: 'GET', credentials: 'include' },
    'Не удалось загрузить список интересов'
  );
  return (await response.json()) as unknown;
}

export async function getFriendProfileSkillsRaw(): Promise<unknown> {
  const response = await skillsFetch(
    buildUrl('/friend/profile/skills'),
    { method: 'GET', credentials: 'include' },
    'Не удалось загрузить интересы'
  );
  return (await response.json()) as unknown;
}

export async function getSponsorProfileSkillsRaw(): Promise<unknown> {
  const response = await skillsFetch(
    buildUrl('/sponsor/profile/skills'),
    { method: 'GET', credentials: 'include' },
    'Не удалось загрузить интересы'
  );
  return (await response.json()) as unknown;
}

export async function setFriendProfileSkillsRaw(
  request: SetProfileSkillsRequest
): Promise<unknown> {
  const response = await skillsFetch(
    buildUrl('/friend/profile/skills'),
    {
      method: 'POST',
      credentials: 'include',
      headers: csrfHeaders(),
      body: JSON.stringify({ skill_ids: [...request.skill_ids] }),
    },
    'Не удалось сохранить интересы'
  );
  return (await response.json()) as unknown;
}

export async function setSponsorProfileSkillsRaw(
  request: SetProfileSkillsRequest
): Promise<unknown> {
  const response = await skillsFetch(
    buildUrl('/sponsor/profile/skills'),
    {
      method: 'POST',
      credentials: 'include',
      headers: csrfHeaders(),
      body: JSON.stringify({ skill_ids: [...request.skill_ids] }),
    },
    'Не удалось сохранить интересы'
  );
  return (await response.json()) as unknown;
}
